<script lang="ts">
  import { _ } from 'svelte-i18n';
  import RequirementGroups from './RequirementGroups.svelte';
  import RaceCapabilityTable from './RaceCapabilityTable.svelte';
  import EventReadinessTable from './EventReadinessTable.svelte';
  import PredictiveAdjust from '../PredictiveMode/PredictiveAdjust.svelte';
  import EventResult from './EventResult.svelte';
  import ConsistencyDonut from './ConsistencyDonut.svelte';
  import LoadIncrease from './LoadIncrease.svelte';
  import ProgressToTarget from './ProgressToTarget.svelte';
  import FocusSettings from './FocusSettings.svelte';
  import KeyTakeaways from './KeyTakeaways.svelte';
  import ConsistencyWeeks from './ConsistencyWeeks.svelte';
  import PowerCurveChart from './PowerCurveChart.svelte';
  import LoadProgressionChart from './LoadProgressionChart.svelte';
  import { num, signed } from './format';
  import { adherenceText, completionPercent, fitnessProgress, isStatusReport, primaryTitle, progressRows, projectionRows } from './report';

  /* eslint-disable @typescript-eslint/no-explicit-any */
  export let report: any;
  export let forUser: string | null = null;
  // Opens Adapt Forecast from a projection that is off its target (a status report, when it is lit); null when not offered.
  export let onAdapt: (() => void) | null = null;
  // A breakthrough proposal was applied (the history is recalculated in the background) or put aside.
  export let onApplied: () => void = () => {};
  export let onDismissed: () => void = () => {};

  $: isStatus = isStatusReport(report);
  $: status = report?.status_report;
  $: result = report?.actual_event_or_race_result;
  $: exec = report?.execution;
  $: adjustedDiffers = report?.targets?.adjusted?.version_id && report.targets.adjusted.version_id !== report.targets.original.version_id;
  $: progress = fitnessProgress(report);
  $: projection = projectionRows(report);
  $: progressBars = progressRows(report);
  $: title = primaryTitle(report);
</script>

  <!-- Targets and, for an event or race, the day itself: two columns, one on a phone -->
  <div class="mb-6 grid grid-cols-1 gap-4 {result ? 'md:grid-cols-2' : ''}">
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-lg font-semibold mb-3">{isStatus ? $_('report.overview.where_today') : report.program_type === 'GOAL' ? $_('report.overview.goal_result') : $_('report.overview.targets')}</h2>
      {#if isStatus}
        <p class="text-sm text-zinc-500 mb-2">{$_('report.overview.projection_intro')}</p>
        <dl class="text-sm text-zinc-500 mb-3 space-y-1">
          <div><dt class="inline font-medium text-zinc-700">{$_('report.table.now')}</dt> <dd class="inline">{$_('report.overview.def_now')}</dd></div>
          <div><dt class="inline font-medium text-zinc-700">{$_('report.table.target')}</dt> <dd class="inline">{$_('report.overview.def_target')}</dd></div>
          <div><dt class="inline font-medium text-zinc-700">{$_('report.overview.projected')}</dt> <dd class="inline">{$_('report.overview.def_projected')}</dd></div>
        </dl>
        <RequirementGroups rows={projection} programType={report.program_type} actualLabel={$_('report.overview.projected')} targetLabel={$_('report.table.target')} {onAdapt} projected />
      {:else}
        {#if report.program_type === 'RACE' && report.race_hero}
          <RaceCapabilityTable hero={report.race_hero} />
        {/if}
        <RequirementGroups rows={report.targets.original.requirements} programType={report.program_type} showCapability={!(report.program_type === 'RACE' && report.race_hero)} />
        {#if report.program_type === 'RACE' && report.race_hero?.readiness}
          <EventReadinessTable readiness={report.race_hero.readiness} />
        {/if}
        {#if adjustedDiffers && report.targets.adjusted.requirements.length}
          <h3 class="text-sm font-medium mt-5 mb-1 text-zinc-800">{$_('report.overview.after_adapted')}</h3>
          <RequirementGroups rows={report.targets.adjusted.requirements} programType={report.program_type} />
        {/if}
      {/if}
    </section>
    {#if result}
      <section class="rounded border border-zinc-200 bg-white p-4">
        <h2 class="text-lg font-semibold mb-2">{report.program_type === 'RACE' ? $_('report.overview.race_day') : $_('report.overview.event_day')}</h2>
        <EventResult {result} programType={report.program_type} {forUser} />
      </section>
    {/if}
  </div>

  {#if isStatus && status?.predictive}
    <PredictiveAdjust part="proposal" predictive={status.predictive} predictiveMode={report.predictive_mode} {forUser} on:applied={onApplied} on:dismissed={onDismissed} />
  {/if}

  {#if isStatus && status}
    <section class="mb-6 rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-lg font-semibold mb-2">{$_('report.overview.training_so_far')}</h2>
      <p class="text-sm mb-3">{adherenceText(status.adherence?.label ?? 'NO_DATA', completionPercent(status.adherence?.completion))}</p>
      {#if status.remaining}
        <p class="text-sm text-zinc-600">{$_('report.overview.still_ahead', { values: { days: status.remaining.training_days, low: num(status.remaining.prescribed_xss.low), high: num(status.remaining.prescribed_xss.high), peak: num(status.remaining.prescribed_xss.peak) } })}</p>
      {/if}
      {#if status.pace?.length}
        <table class="w-full text-sm mt-3">
          <thead><tr class="text-left text-zinc-500"><th class="font-normal py-1">{$_('report.overview.signature')}</th><th class="font-normal text-right">{$_('report.overview.start')}</th><th class="font-normal text-right">{$_('report.table.now')}</th><th class="font-normal text-right">{$_('report.overview.predicted_today')}</th></tr></thead>
          <tbody>
            {#each status.pace as p}
              {@const k = p.metric === 'HIE' ? 0.001 : 1}
              <tr class="border-t"><td class="py-1.5">{p.metric}</td><td class="text-right tabular-nums">{num(p.start * k, k < 1 ? 1 : 0)}</td><td class="text-right tabular-nums">{num(p.now * k, k < 1 ? 1 : 0)}</td>
                <td class="text-right tabular-nums">{num(p.predicted_now * k, k < 1 ? 1 : 0)} <span class="text-zinc-400">({signed(p.ahead_of_prediction * k, k < 1 ? 1 : 0)})</span></td></tr>
            {/each}
          </tbody>
        </table>
      {/if}
    </section>
  {/if}

  <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-3">
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{$_('report.overview.consistency')}</h2>
      <ConsistencyDonut consistency={exec?.consistency} inProgress={isStatus} />
    </section>
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{isStatus ? $_('report.overview.signature_so_far') : $_('report.overview.signature_change')}</h2>
      <div class="space-y-3 text-sm">
        {#each progress as p}
          <div class="flex items-baseline justify-between gap-2">
            <span><span class="text-zinc-600">{p.label === 'TP' ? $_('report.overview.tp') : p.label === 'HIE' ? $_('report.overview.hie') : $_('report.overview.pp')}</span><br /><span class="tabular-nums">{num(p.start * p.scale, p.scale < 1 ? 1 : 0)} → {num(p.finish * p.scale, p.scale < 1 ? 1 : 0)} {p.unit}</span></span>
            <span class="tabular-nums font-medium {p.finish - p.start > 0 ? 'text-green-600' : 'text-zinc-600'}">{signed((p.finish - p.start) * p.scale, p.scale < 1 ? 1 : 0)} {p.unit}</span>
          </div>
        {/each}
      </div>
    </section>
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{isStatus ? $_('report.overview.load_so_far') : $_('report.overview.load_change')}</h2>
      <LoadIncrease start={report.training_load?.start} finish={isStatus ? status?.current : report.training_load?.finish} />
    </section>
  </div>

  <div class="mb-6 grid grid-cols-1 gap-4 {report.program_type === 'EVENT' ? 'md:grid-cols-2' : 'md:grid-cols-3'}">
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{title}</h2>
      <ProgressToTarget rows={progressBars} finishLabel={isStatus ? $_('report.table.now') : $_('report.overview.achieved')} />
    </section>
    {#if report.program_type !== 'EVENT'}
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-1">{$_('report.overview.power_curve')}</h2>
      {#if report.power_curve}
        <PowerCurveChart curve={report.power_curve} endLabel={isStatus ? $_('report.table.now') : $_('report.overview.end')} />
      {:else}
        <p class="text-sm text-zinc-500">{$_('report.overview.curve_needs')}</p>
      {/if}
    </section>
    {/if}
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{report.program_type === 'GOAL' ? $_('report.overview.focus_settings') : $_('report.overview.plan_settings')}</h2>
      <FocusSettings settings={report.settings} hero={report.hero} />
    </section>
  </div>

  {#if exec?.daily_component_compliance?.length}
    <section class="mb-6 rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{$_('report.overview.consistency_calendar')}</h2>
      <ConsistencyWeeks days={exec.daily_component_compliance} />
    </section>
  {/if}

  <div class="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-1">{$_('report.overview.load_through')}</h2>
      <LoadProgressionChart series={report.training_load?.time_series ?? []} />
    </section>
    <section class="rounded border border-zinc-200 bg-white p-4">
      <h2 class="text-base font-semibold mb-3">{$_('report.overview.takeaways')}</h2>
      <KeyTakeaways items={report.takeaways ?? []} />
    </section>
  </div>

  {#if isStatus && status?.predictive}
    <PredictiveAdjust part="notice" predictive={status.predictive} predictiveMode={report.predictive_mode} {forUser} />
  {/if}
